import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import WikiHeader from "./WikiHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WikiUpload() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [username, setUsername] = useState("");
  const [uploadedImages, setUploadedImages] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("wiki_token");
    
    if (!token) {
      setIsLoading(false);
      navigate("/wiki/login");
      return;
    }

    try {
      const response = await axios.get(`${API}/wiki/verify?token=${token}`);
      if (response.data.valid) {
        setIsAuthenticated(true);
        setUsername(response.data.username);
        fetchImages(token);
      } else {
        localStorage.removeItem("wiki_token");
        navigate("/wiki/login");
      }
    } catch (error) {
      console.error("Auth check error:", error);
      navigate("/wiki/login");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchImages = async (token) => {
    try {
      const response = await axios.get(`${API}/wiki/images?token=${token}`);
      setUploadedImages(response.data.images || []);
    } catch (error) {
      console.error("Error fetching images:", error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("wiki_token");
    localStorage.removeItem("wiki_username");
    setIsAuthenticated(false);
    toast.success("Logged out successfully");
    navigate("/wiki/login");
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const token = localStorage.getItem("wiki_token");
    if (!token) {
      toast.error("Please log in to upload images");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/wiki/upload?token=${token}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        toast.success("Image uploaded successfully!");
        fetchImages(token);
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const copyWikicode = (filename) => {
    navigator.clipboard.writeText(`[[File:${filename}]]`);
    toast.success("Wikicode copied to clipboard!");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f6f6f6] flex items-center justify-center">
        <div className="text-[#72777d]">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#f6f6f6]">
      <WikiHeader username={username} onLogout={handleLogout} showSearch={true} />

      {/* Main Content */}
      <div className="max-w-[1200px] mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar */}
          <aside className="w-[160px] flex-shrink-0">
            <nav className="text-sm">
              <div className="mb-4">
                <h3 className="font-bold text-[#202122] mb-2">Navigation</h3>
                <ul className="space-y-1">
                  <li>
                    <Link to="/wiki" className="text-[#3366cc] hover:underline">Main page</Link>
                  </li>
                  <li>
                    <Link to="/" className="text-[#3366cc] hover:underline">Marketing Site</Link>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-[#202122] mb-2">Tools</h3>
                <ul className="space-y-1">
                  <li>
                    <Link to="/wiki" className="text-[#3366cc] hover:underline">All pages</Link>
                  </li>
                  <li>
                    <span className="text-[#72777d]">Upload file</span>
                  </li>
                </ul>
              </div>
            </nav>
          </aside>

          {/* Content */}
          <main className="flex-1 bg-white border border-[#a2a9b1] p-6">
            <h1 className="font-wiki text-3xl text-[#202122] mb-4 border-b border-[#a2a9b1] pb-3">
              Upload file
            </h1>
            
            <div className="mb-8">
              <p className="text-[#202122] mb-4">
                Upload images to use in wiki pages. Supported formats: JPEG, PNG, GIF, WebP, SVG.
              </p>
              
              <div className="bg-[#f8f9fa] border border-[#a2a9b1] p-4 mb-4">
                <label className="block mb-2 font-bold text-[#202122]">Source filename:</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                  className="mb-4 text-sm"
                  disabled={isUploading}
                />
                {isUploading && (
                  <p className="text-[#72777d] text-sm">Uploading...</p>
                )}
              </div>
              
              <div className="bg-[#eaf3ff] border border-[#a7d7f9] p-4">
                <h3 className="font-bold text-[#202122] mb-2">How to use images in pages:</h3>
                <ul className="text-sm text-[#202122] space-y-1">
                  <li><code className="bg-[#f8f9fa] px-1">[[File:filename.jpg]]</code> - Basic image</li>
                  <li><code className="bg-[#f8f9fa] px-1">[[File:filename.jpg|thumb|Caption text]]</code> - Thumbnail with caption</li>
                  <li><code className="bg-[#f8f9fa] px-1">[[File:filename.jpg|200px|right|Caption]]</code> - Sized and aligned</li>
                </ul>
                
                <h3 className="font-bold text-[#202122] mt-4 mb-2">Gallery syntax:</h3>
                <pre className="text-sm text-[#202122] bg-[#f8f9fa] p-2 overflow-x-auto">{`<gallery mode="packed" heights="150px">
File:Image1.jpg|Caption 1
File:Image2.png|Caption 2
File:Image3.jpg|Caption 3
</gallery>`}</pre>
                <p className="text-xs text-[#72777d] mt-2">Options: mode (packed, traditional, nolines), heights, perrow</p>
              </div>
            </div>

            <h2 className="font-wiki text-xl text-[#202122] mt-8 mb-4 border-b border-[#a2a9b1] pb-2">
              Uploaded files
            </h2>
            
            {uploadedImages.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {uploadedImages.map((img, idx) => (
                  <div key={idx} className="border border-[#a2a9b1] p-2 bg-[#f8f9fa]">
                    <img 
                      src={`${API}/wiki/image/${img.filename}`} 
                      alt={img.filename}
                      className="w-full h-32 object-cover mb-2"
                    />
                    <p className="text-xs text-[#202122] truncate mb-1" title={img.filename}>
                      {img.filename}
                    </p>
                    <button
                      onClick={() => copyWikicode(img.filename)}
                      className="text-xs text-[#3366cc] hover:underline"
                    >
                      Copy wikicode
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#72777d] italic">No files have been uploaded yet.</p>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
